import React, { useState } from 'react';
import {
  FiArrowLeft,
  FiArrowRight,
  FiArrowUpCircle,
  FiAward,
  FiBookOpen,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiFileText,
  FiMapPin,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const COPY = {
  en: {
    learning: {
      benefits: [
        ['Know your strengths and what to improve', 'See the skills you already have and which ones you need to improve for the job you want.'],
        ['Build the skills employers are looking for', 'After we show you which skills to improve, complete a course and exercises designed to build each skill.'],
        ['Earn badges and certificates', 'Get skill badges on your profile that employers can see, plus certificates when you finish.'],
      ],
      target: 'Product Marketing Manager',
      pathLabel: 'Your CV compared with this job',
      strengthsLabel: 'Your strengths',
      strengths: ['Campaign planning', 'Product positioning'],
      improvementsLabel: 'What you must improve',
      improvements: ['Marketing analytics', 'Data storytelling'],
      skill: 'SQL for Finance Analysis',
      skillLabel: 'Skill in this pathway',
      course: 'Full Data Analysis Course',
      courseStatus: 'Selected course',
      task: 'SQL Revenue Check',
      taskStatus: 'Exercise',
      badge: 'Marketing Analytics',
      badgeLabel: 'Visible to employers on your profile',
      certificate: 'Pathway certificate',
      certificateLabel: 'Earned when you finish the pathway',
      outcome: 'Show employers the skills you have built',
    },
    interview: {
      benefits: [
        ['Practice speaking your answers', 'Answer real interview questions out loud before your actual interview.'],
        ['See exactly how to improve your answers', 'After each practice interview, see what was missing in each answer and what to say differently next time.'],
      ],
      role: 'Marketing Manager interview',
      live: 'Interview in progress',
      completed: 'Completed',
      yourTurn: 'Your turn',
      question: 'Tell me about a marketing campaign that was not performing as expected. How did you identify the problem, what changes did you make, and what was the result?',
      answerLabel: 'Your answer',
      report: 'Advice for stronger answers',
      suggestions: [
        'Answer the question directly in your first sentence.',
        'Use one real example and explain what you personally did.',
        'Finish with a clear result, using numbers when possible.',
      ],
    },
    radar: {
      benefits: [
        ['Find your best way into the company', 'See which role, team and office fit you best, and what could hold you back.'],
        ['Improve your chances step by step', 'Build missing skills, create the right work samples and connect with employees who may help you get referred.'],
        ['Check the facts before you apply', 'See office locations, visa support, hiring news and the sources behind every finding.'],
      ],
      company: 'Careem',
      fit: '84% · Strong match',
      bestRole: 'Best job for you',
      bestRoleValue: 'Senior Product Marketing Manager',
      strength: 'Why you fit',
      strengthValue: 'You have led regional product launches and growth campaigns',
      gap: 'What could hold you back',
      gapValue: 'You need stronger marketplace analytics examples',
      entry: 'Your best entry point',
      entryValue: 'Product Marketing · MENA team',
      progress: '1 of 3 steps completed',
      actions: ['Build a Careem-style market launch case study', 'Improve your marketplace analytics', 'Connect with MENA team members and work toward a referral'],
      office: 'Best location for you',
      officeValue: 'Dubai, UAE · MENA headquarters',
      visa: 'UAE visa sponsorship',
      visaValue: 'Careem does not confirm sponsorship for this role. You may need an existing UAE work permit.',
      news: 'Hiring signal',
      newsValue: 'Careem is growing its MENA Product Marketing team in Dubai',
      sources: 'Based on 6 sources',
    },
  },
  ar: {
    learning: {
      benefits: [
        ['اعرف نقاط قوتك وما يجب تحسينه', 'تعرّف على المهارات التي تمتلكها والمهارات التي تحتاج إلى تحسينها للوظيفة التي تريدها.'],
        ['طوّر المهارات التي يبحث عنها أصحاب العمل', 'بعد أن نوضح لك المهارات التي تحتاج إلى تحسينها، أكمل دورة وتمارين مصممة لتطوير كل مهارة.'],
        ['احصل على شارات وشهادات', 'أضف شارات مهارات إلى ملفك الشخصي ليراها أصحاب العمل، واحصل على شهادات عند إكمال المسار.'],
      ],
      target: 'مدير تسويق المنتجات',
      pathLabel: 'مقارنة سيرتك الذاتية بهذه الوظيفة',
      strengthsLabel: 'نقاط قوتك',
      strengths: ['تخطيط الحملات', 'تحديد موقع المنتج'],
      improvementsLabel: 'ما يجب تحسينه',
      improvements: ['تحليلات التسويق', 'شرح البيانات'],
      skill: 'تحليل البيانات المالية باستخدام SQL',
      skillLabel: 'المهارة في هذا المسار',
      course: 'دورة كاملة في تحليل البيانات',
      courseStatus: 'الدورة المختارة',
      task: 'فحص الإيرادات باستخدام SQL',
      taskStatus: 'التمرين',
      badge: 'تحليلات التسويق',
      badgeLabel: 'يراها أصحاب العمل في ملفك الشخصي',
      certificate: 'شهادة المسار',
      certificateLabel: 'تحصل عليها عند إكمال المسار',
      outcome: 'أظهر لأصحاب العمل المهارات التي طورتها',
    },
    interview: {
      benefits: [
        ['تدرّب على قول إجاباتك', 'أجب بصوتك عن أسئلة مقابلة حقيقية قبل مقابلتك الفعلية.'],
        ['اعرف بالضبط كيف تحسّن إجاباتك', 'بعد كل مقابلة تدريبية، اعرف ما كان ناقصاً في كل إجابة وما الذي يجب أن تقوله بشكل مختلف في المرة القادمة.'],
      ],
      role: 'مقابلة مدير تسويق',
      live: 'المقابلة جارية',
      completed: 'مكتملة',
      yourTurn: 'دورك',
      question: 'حدّثني عن حملة تسويقية لم تحقق النتائج المتوقعة. كيف حددت المشكلة، وما التغييرات التي أجريتها، وما النتيجة؟',
      answerLabel: 'إجابتك',
      report: 'نصائح لإجابات أقوى',
      suggestions: [
        'أجب عن السؤال مباشرة في الجملة الأولى.',
        'استخدم مثالاً حقيقياً واشرح ما قمت به أنت شخصياً.',
        'اختم بنتيجة واضحة، واستخدم الأرقام عندما يكون ذلك ممكناً.',
      ],
    },
    radar: {
      benefits: [
        ['اعرف أفضل طريق لك إلى الشركة', 'شاهد الوظيفة والفريق ومكتب الشركة الأنسب لك، وما الذي قد يعيقك.'],
        ['حسّن فرصك خطوة بخطوة', 'طوّر المهارات الناقصة، وأنشئ نماذج العمل المناسبة، وتواصل مع موظفين قد يساعدونك في الحصول على ترشيح.'],
        ['تحقق من المعلومات قبل التقديم', 'شاهد مواقع المكاتب ودعم التأشيرة وأخبار التوظيف والمصادر وراء كل معلومة.'],
      ],
      company: 'كريم',
      fit: '84٪ · تطابق قوي',
      bestRole: 'أفضل وظيفة لك',
      bestRoleValue: 'مدير أول لتسويق المنتجات',
      strength: 'لماذا أنت مناسب',
      strengthValue: 'لديك خبرة في إطلاق المنتجات وحملات النمو في المنطقة',
      gap: 'ما قد يعيقك',
      gapValue: 'تحتاج إلى أمثلة أقوى على تحليل بيانات الأسواق الرقمية',
      entry: 'أفضل نقطة دخول لك',
      entryValue: 'فريق تسويق المنتجات · منطقة الشرق الأوسط وشمال أفريقيا',
      progress: 'اكتملت خطوة واحدة من 3',
      actions: ['أنشئ دراسة حالة لإطلاق منتج بأسلوب كريم', 'طوّر مهارات تحليل بيانات الأسواق الرقمية', 'تواصل مع أعضاء الفريق في المنطقة واعمل للحصول على ترشيح'],
      office: 'أفضل موقع لك',
      officeValue: 'دبي، الإمارات · المقر الإقليمي',
      visa: 'رعاية تأشيرة الإمارات',
      visaValue: 'لا تؤكد كريم رعاية التأشيرة لهذه الوظيفة. قد تحتاج إلى تصريح عمل حالي في الإمارات.',
      news: 'إشارة توظيف',
      newsValue: 'تعمل كريم على توسيع فريق تسويق المنتجات لمنطقة الشرق الأوسط وشمال أفريقيا في دبي',
      sources: 'مبني على 6 مصادر',
    },
  },
};

function PreviewContent({ type, page, preview, showHeader = true }) {
  if (type === 'learning') {
    return (
      <div className="js-tutorial-feature-preview is-learning" aria-hidden="true">
        {page === 0 && <>
          {showHeader && <header><span><FiTarget /></span><div><strong>{preview.target}</strong><small>{preview.pathLabel}</small></div></header>}
          <section className="js-feature-value-preview__strengths-improvements">
            <article className="is-strength">
              <strong>{preview.strengthsLabel}</strong>
              {preview.strengths.map((strength) => <span key={strength}><FiCheck /> {strength}</span>)}
            </article>
            <article className="is-improvement">
              <strong>{preview.improvementsLabel}</strong>
              {preview.improvements.map((improvement) => <span key={improvement}><FiArrowUpCircle /> {improvement}</span>)}
            </article>
          </section>
        </>}
        {page === 1 && <>
          <header className="js-feature-value-preview__learning-step">
            <span><FiTarget /></span>
            <div><small>{preview.skillLabel}</small><strong>{preview.skill}</strong></div>
          </header>
          <section className="js-feature-value-preview__learning-work">
            <article><span><FiBookOpen /></span><div><small>{preview.courseStatus}</small><strong>{preview.course}</strong></div></article>
            <article><span><FiBriefcase /></span><div><small>{preview.taskStatus}</small><strong>{preview.task}</strong></div></article>
          </section>
        </>}
        {page === 2 && <>
          <section className="js-feature-value-preview__credentials">
            <article><FiAward /><div><strong>{preview.badge}</strong><small>{preview.badgeLabel}</small></div></article>
            <article><FiFileText /><div><strong>{preview.certificate}</strong><small>{preview.certificateLabel}</small></div></article>
          </section>
          <p className="js-feature-value-preview__outcome">{preview.outcome}</p>
        </>}
      </div>
    );
  }

  if (type === 'interview') {
    return (
      <div className="js-tutorial-feature-preview is-interview" aria-hidden="true">
        {showHeader && <header><div><strong>{preview.role}</strong><small>{page === 0 ? preview.live : preview.completed}</small></div></header>}
        {page === 0 ? <section className="js-tutorial-feature-preview__voice">
          <blockquote>{preview.question}</blockquote>
          <div><i>{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((bar) => <b key={bar} />)}</i><small>{preview.yourTurn}</small></div>
        </section> : <section className="js-tutorial-feature-preview__report">
          <div className="js-tutorial-feature-preview__answer"><small>{preview.answerLabel}</small><i><b /><b /><b /></i></div>
          <strong>{preview.report}</strong>
          <ol>{preview.suggestions.map((suggestion, index) => <li key={suggestion}><b>{index + 1}</b><span>{suggestion}</span></li>)}</ol>
        </section>}
      </div>
    );
  }

  return (
    <div className="js-tutorial-feature-preview is-radar" aria-hidden="true">
      {showHeader && <header><span><FiUsers /></span><div><strong>{preview.company}</strong><small>{preview.fit}</small></div></header>}
      {page === 0 && <section className="js-feature-value-preview__radar-fit">
        <div><strong>{preview.bestRole}</strong><span>{preview.bestRoleValue}</span></div>
        <div className="is-positive"><strong>{preview.strength}</strong><span><FiCheck /> {preview.strengthValue}</span></div>
        <div className="is-gap"><strong>{preview.gap}</strong><span><FiTrendingUp /> {preview.gapValue}</span></div>
      </section>}
      {page === 1 && <section className="js-feature-value-preview__radar-plan">
        <div><strong>{preview.entry}</strong><span>{preview.entryValue}</span></div>
        <small>{preview.progress}</small>
        <i><b /></i>
        <ol>{preview.actions.map((action, index) => <li key={action}><b>{index + 1}</b><span>{action}</span></li>)}</ol>
      </section>}
      {page === 2 && <section className="js-feature-value-preview__radar-intel">
        <div><FiMapPin /><span><strong>{preview.office}</strong><small>{preview.officeValue}</small></span></div>
        <div><FiShield /><span><strong>{preview.visa}</strong><small>{preview.visaValue}</small></span></div>
        <div><FiTrendingUp /><span><strong>{preview.news}</strong><small>{preview.newsValue}</small></span></div>
        <em>{preview.sources}</em>
      </section>}
    </div>
  );
}

const SUMMARY_ICONS = {
  learning: [FiTarget, FiBriefcase, FiAward],
  interview: [FiUsers, FiTrendingUp],
  radar: [FiUsers, FiTarget, FiShield],
};

export function featureValueBenefits(type, language = 'en') {
  return COPY[language === 'ar' ? 'ar' : 'en'][type]?.benefits || [];
}

function FeatureValuePreview({ type, page = 0, showAll = false, className = '' }) {
  const { language, isRTL } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobilePage, setMobilePage] = useState(0);
  const [desktopPage, setDesktopPage] = useState(0);
  const preview = COPY[language === 'ar' ? 'ar' : 'en'][type];
  if (!preview) return null;

  if (showAll) {
    const benefits = featureValueBenefits(type, language);
    const BackIcon = isRTL ? FiArrowRight : FiArrowLeft;
    const NextIcon = isRTL ? FiArrowLeft : FiArrowRight;
    const controls = language === 'ar'
      ? { expand: 'شاهد ما ستحصل عليه', collapse: 'إخفاء المعاينة', step: 'الخطوة', of: 'من', back: 'رجوع', next: 'التالي', done: 'تم' }
      : { expand: 'See what you get', collapse: 'Hide preview', step: 'Step', of: 'of', back: 'Back', next: 'Next', done: 'Done' };

    return (
      <div className={`js-feature-value-preview is-${type} is-summary ${mobileOpen ? 'is-mobile-open' : ''} ${className}`.trim()}>
        <button
          type="button"
          className="js-feature-value-preview__mobile-toggle"
          aria-expanded={mobileOpen}
          onClick={() => {
            setMobileOpen((current) => !current);
            if (mobileOpen) setMobilePage(0);
          }}
        >
          <span>{mobileOpen ? controls.collapse : controls.expand}</span>
          <FiChevronDown aria-hidden="true" />
        </button>
        <div className="js-feature-value-preview__desktop-showcase">
          <div className="js-feature-value-preview__desktop-benefits">
            {benefits.map(([benefitTitle, benefitBody], index) => {
              const Icon = SUMMARY_ICONS[type][index];
              return (
                <button
                  type="button"
                  className={desktopPage === index ? 'is-active' : ''}
                  aria-pressed={desktopPage === index}
                  onClick={() => setDesktopPage(index)}
                  onMouseEnter={() => setDesktopPage(index)}
                  key={benefitTitle}
                >
                  <span aria-hidden="true"><Icon /></span>
                  <div><strong>{benefitTitle}</strong><small>{benefitBody}</small></div>
                </button>
              );
            })}
          </div>
          <article className="js-feature-value-preview__desktop-stage">
            <PreviewContent type={type} page={desktopPage} preview={preview} showHeader={type === 'interview' || desktopPage === 0} />
          </article>
        </div>
        <div className="js-feature-value-preview__summary-grid">
          {benefits.map(([benefitTitle, benefitBody], index) => {
            const Icon = SUMMARY_ICONS[type][index];
            return (
              <article className={`js-feature-value-preview__summary-card ${mobilePage === index ? 'is-mobile-active' : ''}`} key={benefitTitle}>
                <header className="js-feature-value-preview__summary-copy">
                  <span aria-hidden="true"><Icon /></span>
                  <div>
                    <h2>{benefitTitle}</h2>
                    <p>{benefitBody}</p>
                  </div>
                </header>
                <PreviewContent
                  type={type}
                  page={index}
                  preview={preview}
                  showHeader={type === 'interview' || index === 0}
                />
              </article>
            );
          })}
        </div>
        <footer className="js-feature-value-preview__mobile-footer">
          <div className="js-feature-value-preview__mobile-progress">
            <span>{controls.step} {mobilePage + 1} {controls.of} {benefits.length}</span>
            <div>{benefits.map(([title], index) => <i key={title} className={mobilePage === index ? 'is-active' : ''} />)}</div>
          </div>
          <div className="js-feature-value-preview__mobile-actions">
            {mobilePage > 0 && (
              <button type="button" className="is-secondary" onClick={() => setMobilePage((current) => current - 1)}>
                <BackIcon aria-hidden="true" /> {controls.back}
              </button>
            )}
            <button type="button" className="is-primary" onClick={() => {
              if (mobilePage === benefits.length - 1) {
                setMobileOpen(false);
                setMobilePage(0);
              } else {
                setMobilePage((current) => current + 1);
              }
            }}>
              {mobilePage === benefits.length - 1 ? controls.done : controls.next}
              {mobilePage < benefits.length - 1 && <NextIcon aria-hidden="true" />}
            </button>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className={`js-feature-value-preview is-${type} ${className}`.trim()}>
      <PreviewContent type={type} page={page} preview={preview} />
    </div>
  );
}

export default FeatureValuePreview;
